import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isAxiosError } from "axios";
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Dialog,
  DialogContent, DialogTitle, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { useApiService, useIdpService } from "@art/shared-ui";
import type { RootState } from "../../../store";
import { useAttributePermission, usePermission } from "../../permissions";
import { UpgradeRestoreSelectionDialog } from "../billing-settings/helpers";

type PlatformLimit = {
  key: string;
  label: string;
  scope: "tenant" | "account";
  effective_limit: number | null;
  source: "default" | "override" | "unconfigured";
  usage: number | null;
};
type RestoreStatus = {
  projects: { id: string; title?: string; project_key?: string; created_at?: string }[];
  project_limit: { limit: number; current_active: number; headroom: number };
};
const managementUsageKeys = ["PROJECT_CREATION_LIMIT", "ENVIRONMENT_CREATION_LIMIT"];
const validUsage = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const formatLimit = (value: number | null) => value === null ? "Not configured" : value === -1 ? "Unlimited" : value.toLocaleString();
const messageOf = (error: unknown, fallback: string) => isAxiosError<{ message?: string }>(error) ? error.response?.data?.message || fallback : fallback;

function PlatformLimitsContent({ tenantName }: { tenantName: string }) {
  const idp = useIdpService();
  const api = useApiService();
  const canViewRecovery = useAttributePermission("PLATFORM_SETTINGS", "PROJECT_MANAGEMENT", "ViewProjectRestoreStatus");
  const canRestoreProjects = useAttributePermission("PLATFORM_SETTINGS", "PROJECT_MANAGEMENT", "RestoreProjects");
  const canRestore = canViewRecovery && canRestoreProjects;
  const [limits, setLimits] = useState<PlatformLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [usageError, setUsageError] = useState("");
  const [restorable, setRestorable] = useState<RestoreStatus | null>(null);
  const [checkingRecovery, setCheckingRecovery] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");
  const [recoveryInfo, setRecoveryInfo] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setUsageError("");
    const load = async () => {
      const [limitsResult, usageResult] = await Promise.allSettled([
        idp.get(`api/tenants/${encodeURIComponent(tenantName)}/platform-limits`, { showErrorSnack: false }),
        api.get("/platform-limits/usage", { showErrorSnack: false }),
      ]);
      if (!active) return;
      if (limitsResult.status === "rejected") throw limitsResult.reason;
      const rows = limitsResult.value.data?.data?.limits as PlatformLimit[] | undefined;
      if (!Array.isArray(rows) || !rows.length) throw new Error("Invalid platform limits response");
      const usage = usageResult.status === "fulfilled" ? usageResult.value.data?.data?.usage as Record<string, number> | undefined : undefined;
      if (managementUsageKeys.some((key) => !validUsage(usage?.[key]))) setUsageError("Project and environment usage is unavailable. Their platform limits remain enforced.");
      setLimits(rows.map((quota) => ({ ...quota, usage: managementUsageKeys.includes(quota.key) ? validUsage(usage?.[quota.key]) ? usage![quota.key] : null : quota.usage })));
    };
    void load().catch((cause: unknown) => {
      if (active) { setLimits([]); setError(messageOf(cause, "Unable to load platform limits.")); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [idp, api, tenantName, revision]);

  const checkRecovery = async () => {
    if (!canRestore || checkingRecovery) return;
    setCheckingRecovery(true);
    setRecoveryError("");
    setRecoveryInfo("");
    try {
      const response = await api.get("/projects/restore-status", { showErrorSnack: false });
      const data = response.data?.data as RestoreStatus | undefined;
      if (!data || !Array.isArray(data.projects) || !data.project_limit) throw new Error("Invalid project recovery response");
      if (!data.projects.length) setRecoveryInfo("No projects are awaiting recovery.");
      else setRestorable(data);
    } catch (cause: unknown) {
      setRecoveryError(messageOf(cause, "Unable to check projects available for recovery."));
    } finally { setCheckingRecovery(false); }
  };

  const restoreProjects = async (projectIds: string[]) => {
    if (!canRestore || !projectIds.length || restoring) return;
    setRestoring(true);
    setRecoveryError("");
    try {
      await api.post("/projects/restore", { project_ids: projectIds }, { showErrorSnack: false });
      setRestorable(null);
      setRecoveryInfo("Projects restored within the organisation's platform limit.");
      setRevision((current) => current + 1);
    } catch (cause: unknown) {
      setRecoveryError(messageOf(cause, "Unable to restore the selected projects."));
    } finally { setRestoring(false); }
  };

  return <Stack spacing={3}>
    <Box>
      <Typography variant="h5" gutterBottom>Platform limits</Typography>
      <Typography color="text.secondary">Shared resources have platform limits managed by a super administrator. Application subscription changes do not change these limits or remove existing resources.</Typography>
    </Box>
    {error && <Alert severity="error" action={<Button color="inherit" onClick={() => setRevision((value) => value + 1)}>Retry</Button>}>{error}</Alert>}
    {!error && usageError && <Alert severity="warning">{usageError}</Alert>}
    {loading ? <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress aria-label="Loading platform limits" /></Box> : !error && <Card variant="outlined"><TableContainer><Table aria-label="Platform resource usage and limits">
      <TableHead><TableRow><TableCell>Resource</TableCell><TableCell>Usage</TableCell><TableCell>Limit</TableCell><TableCell>Source</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
      <TableBody>{limits.map((quota) => {
        const capped = quota.effective_limit !== null && quota.effective_limit >= 0;
        const atLimit = capped && quota.usage !== null && quota.usage >= quota.effective_limit!;
        const overLimit = capped && quota.usage !== null && quota.usage > quota.effective_limit!;
        return <TableRow key={quota.key}>
          <TableCell><Typography fontWeight={600}>{quota.label}</Typography><Typography variant="caption" color="text.secondary">{quota.scope === "account" ? "Owned by your account" : "Across this organisation"}</Typography></TableCell>
          <TableCell>{quota.usage === null ? "Unavailable" : quota.usage.toLocaleString()}</TableCell>
          <TableCell>{formatLimit(quota.effective_limit)}</TableCell>
          <TableCell>{quota.source === "override" ? quota.scope === "account" ? "Account override" : "Organisation override" : quota.source === "default" ? "Platform default" : "Not configured"}</TableCell>
          <TableCell><Typography variant="body2" color={atLimit || quota.effective_limit === null ? "warning.main" : "text.secondary"}>{overLimit ? "Over limit — additions blocked" : atLimit ? "At limit — additions blocked" : quota.effective_limit === null ? "Contact a super administrator" : quota.usage === null ? "Usage unavailable" : "Capacity available"}</Typography></TableCell>
        </TableRow>;
      })}</TableBody>
    </Table></TableContainer></Card>}
    {!loading && !error && <Typography variant="body2" color="text.secondary">Existing resources stay available when usage exceeds a limit. Contact a super administrator to request a change. Pending invitations and active human members have separate limits; service accounts do not consume the human member allowance.</Typography>}
    {canRestore && <Card variant="outlined"><CardContent><Stack spacing={2} alignItems="flex-start">
      <Typography variant="h6">Recover archived projects</Typography>
      <Typography variant="body2" color="text.secondary">Projects archived by earlier subscription changes can be recovered within your platform project limit. Recovery does not require an application subscription upgrade.</Typography>
      {recoveryError && !restorable && <Alert severity="error">{recoveryError}</Alert>}
      {recoveryInfo && <Alert severity="success">{recoveryInfo}</Alert>}
      <Button variant="outlined" disabled={checkingRecovery || loading} onClick={() => void checkRecovery()}>{checkingRecovery ? "Checking…" : "Find projects to recover"}</Button>
    </Stack></CardContent></Card>}
    <Dialog open={Boolean(restorable) && canRestore} onClose={() => !restoring && setRestorable(null)} fullWidth maxWidth="md">
      <DialogTitle>Recover archived projects</DialogTitle>
      <DialogContent>{recoveryError && <Alert severity="error" sx={{ mb: 2 }}>{recoveryError}</Alert>}{restorable && <UpgradeRestoreSelectionDialog restoreData={{ ...restorable, channels: [], channel_limits: {} }} isSubmitting={restoring} onSkip={() => setRestorable(null)} onConfirm={(projectIds: string[]) => void restoreProjects(projectIds)} />}</DialogContent>
    </Dialog>
  </Stack>;
}

export default function PlatformLimits() {
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const canRead = usePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "Usage");
  if (!tenantName || !canRead) return <Alert severity="error">You do not have permission to view platform limits.</Alert>;
  return <PlatformLimitsContent key={tenantName} tenantName={tenantName} />;
}
